import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { usePageMeta } from "@/hooks/usePageMeta";
import { useFavorites } from "@/hooks/useFavorites";
import { categories } from "@/mocks/categories";
import { getRelatedVideos, getVideoById } from "@/utils/videoHelpers";
import { formatCount, formatDate } from "@/utils/format";
import VideoCard from "@/components/feature/VideoCard";
import SimulatedPlayer from "./components/SimulatedPlayer";
import CommentSection from "./components/CommentSection";

export default function DetailPage() {
  const { videoId } = useParams();
  const video = videoId ? getVideoById(videoId) : undefined;
  const { isFavorite, toggleFavorite } = useFavorites();
  const [toast, setToast] = useState("");

  usePageMeta({
    title: video ? `${video.title}-91短视频观看与分享` : "视频详情-91短视频",
    description: video
      ? `观看91短视频的${video.title}，涵盖搞笑、娱乐等多种热门短视频内容，支持分享与评论。`
      : "91短视频视频详情页，观看热门短视频并参与评论互动。",
    keywords: video
      ? `${video.title},91短视频,短视频,${video.tags.join(",")}`
      : "91短视频,视频详情,短视频",
  });

  const showToast = (message: string) => {
    setToast(message);
    window.setTimeout(() => setToast(""), 2200);
  };

  if (!video) {
    return (
      <div className="mx-auto flex w-full max-w-3xl flex-col items-center px-4 py-24 text-center">
        <i className="ri-file-unknow-line text-5xl text-foreground-300"></i>
        <h1 className="mt-4 text-xl font-bold text-foreground-900">视频不存在或已被移除</h1>
        <p className="mt-2 text-sm text-foreground-500">
          换个视频看看，或者回到首页发现更多热门内容。
        </p>
        <Link
          to="/"
          className="mt-6 cursor-pointer rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition hover:bg-primary-600"
        >
          返回首页
        </Link>
      </div>
    );
  }

  const category = categories.find((item) => item.key === video.category);
  const related = getRelatedVideos(video, 6);
  const favorited = isFavorite(video.id);

  const handleShare = async () => {
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
      showToast("链接已复制，快去分享给朋友吧！");
    } catch {
      showToast("复制失败，请手动复制浏览器地址栏链接");
    }
  };

  const handleFavorite = () => {
    const nowFavorite = toggleFavorite(video.id);
    showToast(nowFavorite ? "已加入收藏" : "已取消收藏");
  };

  return (
    <div className="mx-auto w-full max-w-7xl px-4 py-8 md:px-6 md:py-10">
      <nav className="mb-4 flex flex-wrap items-center gap-1.5 text-sm text-foreground-500" aria-label="面包屑">
        <Link to="/" className="transition hover:text-primary-600">
          首页
        </Link>
        <i className="ri-arrow-right-s-line"></i>
        {category && (
          <>
            <Link to={`/category/${category.slug}`} className="transition hover:text-primary-600">
              {category.name}短视频
            </Link>
            <i className="ri-arrow-right-s-line"></i>
          </>
        )}
        <span className="truncate font-medium text-foreground-800">{video.title}</span>
      </nav>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="min-w-0 lg:col-span-2">
          <SimulatedPlayer cover={video.cover} title={video.title} duration={video.duration} />

          <h1 className="mt-5 text-xl font-bold leading-snug text-foreground-900 md:text-2xl">
            {video.title}
          </h1>

          <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-foreground-500">
            <span className="flex items-center gap-1.5">
              <i className="ri-calendar-line"></i>
              {formatDate(video.uploadDate)}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-play-circle-line"></i>
              {formatCount(video.plays)} 次播放
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-chat-3-line"></i>
              {formatCount(video.comments)} 条评论
            </span>
            {category && (
              <Link
                to={`/category/${category.slug}`}
                className="flex items-center gap-1.5 rounded-full bg-secondary-100 px-2.5 py-0.5 text-xs font-medium text-secondary-900 transition hover:bg-secondary-200"
              >
                <i className={category.icon}></i>
                {category.name}
              </Link>
            )}
          </div>

          <div className="mt-4 flex items-center justify-between gap-3 rounded-lg border border-background-200 bg-background-50 p-3 md:p-4">
            <div className="flex min-w-0 items-center gap-3">
              <img
                src={video.authorAvatar}
                alt={`${video.author} 头像`}
                title={video.author}
                className="h-10 w-10 rounded-full object-cover"
              />
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-foreground-900">{video.author}</p>
                <p className="text-xs text-foreground-400">
                  {formatCount(video.likes)} 人点赞过这个视频
                </p>
              </div>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <button
                type="button"
                onClick={handleFavorite}
                className={`flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border px-3 py-2 text-sm font-medium transition ${
                  favorited
                    ? "border-accent-300 bg-accent-50 text-accent-700"
                    : "border-background-300 text-foreground-700 hover:border-primary-300 hover:text-primary-600"
                }`}
              >
                <i className={`${favorited ? "ri-heart-fill" : "ri-heart-line"}`}></i>
                {favorited ? "已收藏" : "收藏"}
              </button>
              <button
                type="button"
                onClick={handleShare}
                className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-3 py-2 text-sm font-medium text-background-50 transition hover:bg-primary-600"
              >
                <i className="ri-share-forward-line"></i>
                分享
              </button>
            </div>
          </div>

          <div className="mt-4 rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
            <h2 className="text-sm font-bold text-foreground-900">视频简介</h2>
            <p className="mt-2 text-sm leading-relaxed text-foreground-700">{video.description}</p>
            <div className="mt-3 flex flex-wrap gap-2">
              {video.tags.map((tag) => (
                <Link
                  key={tag}
                  to={`/search?q=${encodeURIComponent(tag)}`}
                  className="cursor-pointer rounded-full bg-background-100 px-3 py-1 text-xs text-foreground-600 transition hover:bg-primary-100 hover:text-primary-700"
                >
                  #{tag}
                </Link>
              ))}
            </div>
          </div>

          <div className="mt-6">
            <CommentSection count={video.comments} />
          </div>
        </div>

        <aside className="min-w-0">
          <div className="rounded-lg border border-background-200 bg-background-50 p-4 lg:sticky lg:top-20">
            <h2 className="flex items-center gap-2 text-base font-bold text-foreground-900">
              <i className="ri-magic-line text-lg text-primary-500"></i>
              相关推荐
            </h2>
            <div className="mt-4 grid grid-cols-2 gap-3 lg:grid-cols-1">
              {related.map((item) => (
                <Link
                  key={item.id}
                  to={`/detail/${item.id}`}
                  className="group flex cursor-pointer gap-3"
                >
                  <div className="relative h-14 w-24 shrink-0 overflow-hidden rounded-md bg-background-200">
                    <img
                      src={item.cover}
                      alt={`${item.title} 封面`}
                      title={`${item.title} - 91短视频`}
                      className="h-full w-full object-cover transition duration-300 group-hover:scale-105"
                    />
                    <span className="absolute bottom-1 right-1 rounded bg-foreground-950/75 px-1 text-[10px] text-background-50">
                      {item.duration}
                    </span>
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="line-clamp-2 text-sm font-medium leading-snug text-foreground-800 transition group-hover:text-primary-600">
                      {item.title}
                    </p>
                    <p className="mt-1 text-xs text-foreground-400">
                      {formatCount(item.plays)} 次播放
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </aside>
      </div>

      {related.length > 0 && (
        <section className="mt-12">
          <h2 className="mb-5 flex items-center gap-2 text-lg font-bold text-foreground-900">
            <i className="ri-play-list-2-line text-xl text-primary-500"></i>
            同类热门视频
          </h2>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-3 md:gap-5 lg:grid-cols-4">
            {related.slice(0, 4).map((item) => (
              <VideoCard key={item.id} video={item} />
            ))}
          </div>
        </section>
      )}

      {toast && (
        <div className="fixed bottom-6 left-1/2 z-50 -translate-x-1/2 animate-fade-up rounded-full bg-foreground-950/90 px-5 py-2.5 text-sm text-background-50">
          {toast}
        </div>
      )}
    </div>
  );
}